import React, { useCallback, useEffect, useMemo, useState } from "react";
import { withRouter } from "react-router-dom";
import { toast } from "react-hot-toast";
import Layout from "../../components/Layout";
import { sideBarItems } from "../../utils";
import AdminService from "../../services/admin/AdminService";
import SubscriptionService from "../../services/admin/SubscriptionService";
import RideShareAdminService from "../../services/admin/RideShareAdminService";
import AuthService from "../../services/owner/AuthService";
import VehicleService from "../../services/owner/VehicleService";
import FileService from "../../services/FileService";
import { Pagination } from "../../components/common/pagination";
import ImageS3 from "../../components/common/ImageS3";
import useAuthentication from "../../components/HOC/useAuthentication";
import CarInfo from "../../components/common/car_info_model";
import { FaTrashAlt } from "react-icons/fa";
import moment from "moment";
import { formatDeviceValue } from "../../utils/deviceInfo";

const SECTION_META = {
  personal: { label: "Personal Data" },
  ocr: { label: "OCR" },
  vehicles: { label: "Vehicle List" },
  income: { label: "Income Statement" },
  payments: { label: "Payments" },
  bids: { label: "Ride Share Bids" },
  device: { label: "Device Info" },
};

const PERSONAL_DOC_LABELS = {
  nidFont: "NID Document",
  drivingLicense: "Driving License",
};

const VEHICLE_DOC_LABELS = {
  vehicleFont: "Vehicle Front",
  vehicleBack: "Vehicle Back",
  vehicleNumberPlate: "Vehicle With Number Plate",
  vehicleRegistrationPaper: "Registration Paper",
  smartCard: "Smart Card",
  taxToken: "Tax Token",
  fitness: "Fitness Paper",
};

const BID_STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Accepted", value: "accepted" },
  { label: "Rejected", value: "rejected" },
  { label: "Created", value: "created" },
  { label: "Expired", value: "expired" },
  { label: "Cancelled", value: "cancelled_by_system" },
];

const PAYMENT_STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Success", value: "success" },
  { label: "Initiated", value: "initiated" },
  { label: "Failed", value: "failed" },
];

const PAYMENT_MODE_OPTIONS = [
  { label: "All Types", value: "" },
  { label: "Purchase", value: "purchase" },
  { label: "Renewal", value: "renewal" },
  { label: "Manual", value: "manual" },
];

const OCR_DOC_META = {
  nid: { label: "NID OCR", apiKey: "nid" },
  license: { label: "Driving License OCR", apiKey: "license" },
};

const OCR_SOURCE_DOCS = {
  nid: [
    {
      key: "nid-front",
      title: "NID Front",
      docKey: "nidFont",
    },
    {
      key: "nid-back",
      title: "NID Back",
      docKey: "nidBack",
    },
  ],
  license: [
    {
      key: "license-front",
      title: "License Front",
      docKey: "drivingLicense",
    },
    {
      key: "license-back",
      title: "License Back",
      docKey: "drivingLicenseBack",
    },
  ],
};

const OCR_FIELD_CONFIG = {
  nid: [
    { key: "cardType", label: "Card Type" },
    { key: "country", label: "Country" },
    { key: "issuingAuthority", label: "Issuing Authority" },
    { key: "nameEnglish", label: "Name (English)" },
    { key: "nameBengali", label: "Name (Bengali)" },
    { key: "fatherName", label: "Father Name" },
    { key: "motherName", label: "Mother Name" },
    { key: "DOB", label: "Date Of Birth (YYYY-MM-DD)" },
    { key: "idNumber", label: "NID Number" },
    {
      key: "rawText",
      label: "Raw Text Lines (one per line)",
      type: "multiline-array",
    },
  ],
  license: [
    { key: "cardType", label: "Card Type" },
    { key: "country", label: "Country" },
    { key: "issuingAuthority", label: "Issuing Authority" },
    { key: "nameEnglish", label: "Name (English)" },
    { key: "DOB", label: "Date Of Birth (YYYY-MM-DD)" },
    { key: "bloodGroup", label: "Blood Group" },
    { key: "fatherOrHusbandName", label: "Father Or Husband Name" },
    { key: "licenseNumber", label: "License Number" },
    {
      key: "issueOrRenewalDate",
      label: "Issue Or Renewal Date (YYYY-MM-DD)",
    },
    { key: "validityDate", label: "Validity Date (YYYY-MM-DD)" },
    { key: "firstIssueDate", label: "First Issue Date (YYYY-MM-DD)" },
    { key: "referenceNumber", label: "Reference Number" },
    { key: "address", label: "Address", type: "textarea" },
    {
      key: "authorizedVehicleClasses",
      label: "Authorized Vehicle Classes (one per line)",
      type: "multiline-array",
    },
    {
      key: "rawText",
      label: "Raw Text Lines (one per line)",
      type: "multiline-array",
    },
  ],
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString();
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "BDT 0.00";
  return `BDT ${amount.toFixed(2)}`;
};

const getPaymentTypeLabel = (payment) => {
  const type = String(payment?.transactionType || "").toLowerCase();
  if (type === "subscription_purchase") return "Purchase";
  if (type === "subscription_renewal") return "Renewal";
  if (type === "subscription_manual") return "Manual";
  return "N/A";
};

const formatBoolean = (value) => (value ? "Yes" : "No");

const getError = (err) => err?.response?.data?.error || "Operation failed";

const isDirectDocumentUrl = (value) =>
  typeof value === "string" &&
  (value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:image/"));

const normalizeDocumentUrl = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("data:image/")) return trimmed;
  try {
    return encodeURI(trimmed);
  } catch (error) {
    return trimmed;
  }
};

const getDisplayName = (driver) => {
  if (!driver) return "N/A";
  const combined = [driver.firstName, driver.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return driver.name || combined || driver.username || driver.mobile || "N/A";
};

const getImageUrl = (value) =>
  value ||
  "https://uturn-s3-bucket.s3.ap-southeast-1.amazonaws.com/uturn_defaults/male_avatar.png";

const normalizeOcrValue = (value, type) => {
  if (type === "multiline-array") {
    if (!Array.isArray(value)) return "";
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .join("\n");
  }

  if (typeof value === "undefined" || value === null) return "";
  return String(value);
};

const buildOcrFormState = (documentType, extractedData = {}) => {
  const config = OCR_FIELD_CONFIG[documentType] || [];
  return config.reduce((acc, field) => {
    acc[field.key] = normalizeOcrValue(extractedData?.[field.key], field.type);
    return acc;
  }, {});
};

const buildOcrPayload = (documentType, formState = {}) => {
  const config = OCR_FIELD_CONFIG[documentType] || [];
  return config.reduce((acc, field) => {
    const raw = formState?.[field.key];

    if (field.type === "multiline-array") {
      const values = String(raw || "")
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean);
      acc[field.key] = values;
      return acc;
    }

    const text = String(raw || "").trim();
    acc[field.key] = text || null;
    return acc;
  }, {});
};

const InfoRow = ({ label, value }) => (
  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
    <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    <div className="mt-1 text-sm font-medium text-gray-900 break-words">
      {value || "N/A"}
    </div>
  </div>
);

const SectionButton = ({ active, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-md text-sm font-medium transition ${
      active
        ? "bg-indigo-600 text-white"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
    }`}
  >
    {label}
  </button>
);

const DocumentPreview = ({ uri, title }) => {
  const [previewSrc, setPreviewSrc] = useState("");

  useEffect(() => {
    let mounted = true;

    const resolvePreview = async () => {
      if (!uri) {
        if (mounted) setPreviewSrc("");
        return;
      }

      const normalizedUri = normalizeDocumentUrl(uri);

      try {
        const resolved = await FileService.getDownloadableLink(normalizedUri);
        if (mounted && resolved) {
          setPreviewSrc(resolved);
          return;
        }
      } catch (error) {
        // Fall through to direct URL rendering.
      }

      if (mounted) {
        setPreviewSrc(normalizedUri);
      }
    };

    resolvePreview();

    return () => {
      mounted = false;
    };
  }, [uri]);

  if (!uri) {
    return <span className="text-sm text-gray-500">No document uploaded</span>;
  }

  if (!previewSrc) {
    return <span className="text-sm text-gray-500">Loading preview...</span>;
  }

  return (
    <img
      src={previewSrc}
      alt={title}
      className="w-full h-52 object-contain"
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => {
        const fallback = normalizeDocumentUrl(uri);
        if (isDirectDocumentUrl(fallback) && fallback !== previewSrc) {
          setPreviewSrc(fallback);
        }
      }}
    />
  );
};

const ImageCard = ({ title, uri, identity }) => (
  <div className="border rounded-lg overflow-hidden bg-white">
    <div className="px-3 py-2 border-b bg-gray-50">
      <div className="text-sm font-medium text-gray-900">{title}</div>
      {identity && (
        <div className="text-xs text-gray-500 break-all">ID: {identity}</div>
      )}
    </div>
    <div className="h-52 bg-gray-100 flex items-center justify-center">
      <DocumentPreview uri={uri} title={title} />
    </div>
    {uri && (
      <div className="px-3 py-2 border-t bg-white">
        <a
          href={uri}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-indigo-600 hover:text-indigo-800"
        >
          Open document
        </a>
      </div>
    )}
  </div>
);

const DeviceInfoSection = ({ driver }) => {
  const devices = [...(driver?.firebaseTokens || [])].sort((a, b) => {
    if (a?.active && !b?.active) return -1;
    if (!a?.active && b?.active) return 1;
    return (
      new Date(b?.lastLoginAt || b?.setDate || 0) -
      new Date(a?.lastLoginAt || a?.setDate || 0)
    );
  });

  if (!devices.length) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Device Info
        </h2>
        <p className="text-sm text-gray-500">
          No device information found for this driver.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {devices.map((device, index) => {
        const info = device?.deviceInfo || {};
        const title =
          [info.brand, info.model].filter(Boolean).join(" ") ||
          `Device ${index + 1}`;
        const rows = [
          ["Status", device?.active ? "Active" : "Inactive"],
          ["FCM Token", device?.token],
          ["Login Count", device?.loginCount || 0],
          [
            "Last Login",
            device?.lastLoginAt
              ? moment(device.lastLoginAt).format("DD MMM YYYY hh:mm A")
              : "N/A",
          ],
          [
            "Token Set Date",
            device?.setDate
              ? moment(device.setDate).format("DD MMM YYYY hh:mm A")
              : "N/A",
          ],
          ...Object.entries(info).map(([key, value]) => [
            key,
            formatDeviceValue(key, value),
          ]),
        ];

        return (
          <div
            key={`${device?.token || index}`}
            className="bg-white shadow-md rounded-lg overflow-hidden"
          >
            <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {title}
                </h2>
                <p className="text-sm text-gray-500">
                  {info.systemName || "Unknown OS"} {info.systemVersion || ""}
                </p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  device?.active
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-200 text-gray-700"
                }`}
              >
                {device?.active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-px bg-gray-200">
              {rows.map(([label, value]) => (
                <div key={label} className="bg-white p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    {label}
                  </div>
                  <pre className="mt-1 text-sm font-sans text-gray-900 whitespace-pre-wrap break-words">
                    {value || "N/A"}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

function DriverAdminDetails(props) {
  const { history, location, match } = props;
  const { driverId, section } = match.params;
  const initialQuery = new URLSearchParams(location.search);
  const { user: authUser } = useAuthentication();

  const currentSection = useMemo(() => {
    if (location.pathname.startsWith("/ridesharebids/driver/")) {
      return "bids";
    }
    if (section && SECTION_META[section]) {
      return section;
    }
    return "personal";
  }, [location.pathname, section]);

  const [driver, setDriver] = useState(null);
  const [driverLoading, setDriverLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);

  const [incomeData, setIncomeData] = useState(null);
  const [incomeLoading, setIncomeLoading] = useState(false);
  const [incomePage, setIncomePage] = useState(1);
  const [incomeLimit, setIncomeLimit] = useState(20);
  const [incomeTotal, setIncomeTotal] = useState(0);
  const [incomeTotalPages, setIncomeTotalPages] = useState(1);
  const [incomeFilter, setIncomeFilter] = useState({
    fromDate: "",
    toDate: "",
  });

  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentLimit, setPaymentLimit] = useState(20);
  const [paymentTotal, setPaymentTotal] = useState(0);
  const [paymentTotalPages, setPaymentTotalPages] = useState(1);
  const [paymentFilters, setPaymentFilters] = useState({
    status: "",
    mode: "",
    fromDate: "",
    toDate: "",
    search: "",
  });

  const [bids, setBids] = useState([]);
  const [bidSummary, setBidSummary] = useState(null);
  const [bidLoading, setBidLoading] = useState(false);
  const [bidTotal, setBidTotal] = useState(0);
  const [bidTotalPages, setBidTotalPages] = useState(1);
  const [bidPage, setBidPage] = useState(1);
  const [bidLimit, setBidLimit] = useState(20);
  const [bidFilters, setBidFilters] = useState({
    status: "",
    fromDate: initialQuery.get("fromDate") || "",
    toDate: initialQuery.get("toDate") || "",
  });

  const [ocrData, setOcrData] = useState({
    nid: null,
    license: null,
  });
  const [ocrForm, setOcrForm] = useState({
    nid: buildOcrFormState("nid"),
    license: buildOcrFormState("license"),
  });
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrSaving, setOcrSaving] = useState({
    nid: false,
    license: false,
  });
  const [profileVerifyLoading, setProfileVerifyLoading] = useState(false);
  const [vehicleManageModalOpen, setVehicleManageModalOpen] = useState(false);
  const [selectedVehicleEntry, setSelectedVehicleEntry] = useState(null);

  const adminId = useMemo(
    () => authUser?.user?._id || authUser?._id || null,
    [authUser],
  );

  const isRideShareDriver = useMemo(() => {
    if (!driver?.isDriver) return false;

    const hasRideShareService = Boolean(driver?.serviceType?.rideShare);
    const hasRideShareStatusFlag =
      typeof driver?.rideShareStatus?.isApproved === "boolean" ||
      typeof driver?.rideShareStatus?.isOnline === "boolean";
    const hasRideShareStats =
      Number(driver?.rideShareStats?.totalRides || 0) > 0;

    return hasRideShareService || hasRideShareStatusFlag || hasRideShareStats;
  }, [driver]);

  const goToSection = useCallback(
    (nextSection) => {
      if (nextSection === "bids") {
        history.push(`/ridesharebids/driver/${driverId}`);
        return;
      }
      history.push(`/subscriptiondriver/${driverId}/${nextSection}`);
    },
    [driverId, history],
  );

  const vehicleRows = useMemo(() => {
    const profileVehicles = Array.isArray(driver?.vehicles)
      ? driver.vehicles
      : [];
    const apiVehicles = Array.isArray(vehicles)
      ? vehicles.map((item) => ({ vehicle: item }))
      : [];
    const byId = {};

    [...profileVehicles, ...apiVehicles].forEach((item) => {
      const actualVehicle = item?.vehicle || item;
      const vehicleId = actualVehicle?._id;
      if (vehicleId) {
        byId[vehicleId] = {
          ...(byId[vehicleId] || {}),
          ...(item || {}),
          vehicle: {
            ...((byId[vehicleId] || {})?.vehicle || {}),
            ...(actualVehicle || {}),
          },
        };
      }
    });

    return Object.values(byId);
  }, [driver, vehicles]);

  const showVehicleInfo = useCallback((vehicleEntry) => {
    setSelectedVehicleEntry(vehicleEntry || null);
    setVehicleManageModalOpen(Boolean(vehicleEntry));
  }, []);

  const handleVehicleModal = useCallback((visible) => {
    setVehicleManageModalOpen(Boolean(visible));
    if (!visible) {
      setSelectedVehicleEntry(null);
    }
  }, []);

  const driverSummaryCards = useMemo(
    () => [
      { label: "Role", value: driver?.role || "N/A" },
      { label: "Mobile", value: driver?.mobile || "N/A" },
      {
        label: "Driver Verified",
        value: driver ? formatBoolean(driver.isVerifiedAsDriver) : "N/A",
      },
      {
        label: "Access Status",
        value: driver?.subscriptionAccessStatus || "active",
      },
    ],
    [driver],
  );

  const loadDriverProfile = useCallback(async () => {
    if (!driverId) return;
    setDriverLoading(true);
    setVehiclesLoading(true);
    try {
      const [profileRes, vehiclesRes] = await Promise.all([
        AdminService.getOwnerProfile(driverId),
        VehicleService.getOwnerVehicles(driverId),
      ]);
      setDriver(profileRes || null);
      setVehicles(Array.isArray(vehiclesRes) ? vehiclesRes : []);
    } catch (err) {
      toast.error(getError(err));
      setDriver(null);
      setVehicles([]);
    } finally {
      setDriverLoading(false);
      setVehiclesLoading(false);
    }
  }, [driverId]);

  const deleteVehicle = useCallback(
    async (vehicleEntry) => {
      const vehicle = vehicleEntry?.vehicle;
      const vehicleId = vehicle?._id;
      if (!vehicleId || !driverId) {
        toast.error("Failed to delete vehicle");
        return;
      }

      try {
        const ownerId = vehicle?.owner || driverId;
        const res = await VehicleService.deleteVehicleByOwner(
          ownerId,
          vehicleId,
        );
        if (res?.status === 200) {
          toast.success("Vehicle deleted successfully");
          await loadDriverProfile();
        } else {
          toast.error("Failed to delete vehicle");
        }
      } catch (error) {
        toast.error("Failed to delete vehicle");
      }
    },
    [driverId, loadDriverProfile],
  );

  const loadIncome = useCallback(
    async (filters = incomeFilter) => {
      if (!driverId) return;
      setIncomeLoading(true);
      try {
        const res = await SubscriptionService.getDriverIncomeHistory(driverId, {
          page: incomePage,
          limit: incomeLimit,
          fromDate: filters.fromDate || undefined,
          toDate: filters.toDate || undefined,
        });
        setIncomeData(res?.data || null);
        setIncomeTotal(Number(res?.data?.pagination?.total || 0));
        setIncomeTotalPages(Number(res?.data?.pagination?.totalPages || 1));
      } catch (err) {
        toast.error(getError(err));
        setIncomeData(null);
        setIncomeTotal(0);
        setIncomeTotalPages(1);
      } finally {
        setIncomeLoading(false);
      }
    },
    [driverId, incomeFilter, incomeLimit, incomePage],
  );

  const loadBids = useCallback(async () => {
    if (!driverId) return;
    setBidLoading(true);
    try {
      const res = await RideShareAdminService.getBidDetails({
        driverId,
        page: bidPage,
        limit: bidLimit,
        status: bidFilters.status || undefined,
        fromDate: bidFilters.fromDate || undefined,
        toDate: bidFilters.toDate || undefined,
      });
      setBids(Array.isArray(res?.data?.data) ? res.data.data : []);
      setBidSummary(res?.data?.summary || null);
      setBidTotal(Number(res?.data?.pagination?.total || 0));
      setBidTotalPages(Number(res?.data?.pagination?.totalPages || 1));
    } catch (err) {
      toast.error(getError(err));
      setBids([]);
      setBidSummary(null);
      setBidTotal(0);
      setBidTotalPages(1);
    } finally {
      setBidLoading(false);
    }
  }, [
    bidFilters.fromDate,
    bidFilters.status,
    bidFilters.toDate,
    bidLimit,
    bidPage,
    driverId,
  ]);

  const loadPayments = useCallback(async () => {
    if (!driverId) return;
    setPaymentLoading(true);
    try {
      const res = await SubscriptionService.getSubscriptionPayments({
        driverId,
        page: paymentPage,
        limit: paymentLimit,
        status: paymentFilters.status || undefined,
        mode: paymentFilters.mode || undefined,
        fromDate: paymentFilters.fromDate || undefined,
        toDate: paymentFilters.toDate || undefined,
        search: paymentFilters.search || undefined,
      });

      setPayments(Array.isArray(res?.data?.data) ? res.data.data : []);
      setPaymentSummary(res?.data?.summary || null);
      setPaymentTotal(Number(res?.data?.pagination?.total || 0));
      setPaymentTotalPages(Number(res?.data?.pagination?.totalPages || 1));
    } catch (err) {
      toast.error(getError(err));
      setPayments([]);
      setPaymentSummary(null);
      setPaymentTotal(0);
      setPaymentTotalPages(1);
    } finally {
      setPaymentLoading(false);
    }
  }, [
    driverId,
    paymentFilters.fromDate,
    paymentFilters.mode,
    paymentFilters.search,
    paymentFilters.status,
    paymentFilters.toDate,
    paymentLimit,
    paymentPage,
  ]);

  const applyOcrRecordsToForm = useCallback((records) => {
    const nextNid = records?.nid?.extractedData || {};
    const nextLicense = records?.license?.extractedData || {};

    setOcrForm({
      nid: buildOcrFormState("nid", nextNid),
      license: buildOcrFormState("license", nextLicense),
    });
  }, []);

  const loadOcrData = useCallback(async () => {
    if (!driverId || !isRideShareDriver) {
      setOcrData({ nid: null, license: null });
      return;
    }

    setOcrLoading(true);
    try {
      const res = await RideShareAdminService.getDriverOcr(driverId);
      const records = res?.data?.data?.documents || {
        nid: null,
        license: null,
      };
      setOcrData(records);
      applyOcrRecordsToForm(records);
      await loadDriverProfile();
    } catch (err) {
      toast.error(getError(err));
      setOcrData({ nid: null, license: null });
      applyOcrRecordsToForm({ nid: null, license: null });
    } finally {
      setOcrLoading(false);
    }
  }, [applyOcrRecordsToForm, driverId, isRideShareDriver, loadDriverProfile]);

  const updateOcrField = useCallback((documentType, key, value) => {
    setOcrForm((prev) => ({
      ...prev,
      [documentType]: {
        ...(prev?.[documentType] || {}),
        [key]: value,
      },
    }));
  }, []);

  const saveOcrDocument = useCallback(
    async (documentType) => {
      if (!driverId) return;

      setOcrSaving((prev) => ({ ...prev, [documentType]: true }));
      try {
        const payload = {
          extractedData: buildOcrPayload(documentType, ocrForm?.[documentType]),
        };
        const res = await RideShareAdminService.updateDriverOcr(
          driverId,
          documentType,
          payload,
        );

        const record = res?.data?.data || null;
        setOcrData((prev) => ({
          ...prev,
          [documentType]: record,
        }));
        applyOcrRecordsToForm({
          ...ocrData,
          [documentType]: record,
        });
        await loadDriverProfile();
        toast.success(
          `${OCR_DOC_META[documentType]?.label || documentType} updated`,
        );
      } catch (err) {
        toast.error(getError(err));
      } finally {
        setOcrSaving((prev) => ({ ...prev, [documentType]: false }));
      }
    },
    [applyOcrRecordsToForm, driverId, loadDriverProfile, ocrData, ocrForm],
  );

  const toggleDriverProfileVerification = useCallback(async () => {
    if (!driverId || !adminId) {
      toast.error("Admin session is required to verify driver profile");
      return;
    }

    setProfileVerifyLoading(true);
    try {
      await AuthService.verifyDriver(adminId, driverId);
      await loadDriverProfile();
      toast.success(
        driver?.isVerifiedAsDriver
          ? "Driver profile verification cancelled"
          : "Driver profile verified",
      );
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setProfileVerifyLoading(false);
    }
  }, [adminId, driver?.isVerifiedAsDriver, driverId, loadDriverProfile]);

  useEffect(() => {
    loadDriverProfile();
  }, [loadDriverProfile]);

  useEffect(() => {
    if (currentSection === "income") {
      loadIncome();
    }
  }, [currentSection, loadIncome]);

  useEffect(() => {
    if (currentSection === "bids") {
      loadBids();
    }
  }, [currentSection, loadBids]);

  useEffect(() => {
    if (currentSection === "payments") {
      loadPayments();
    }
  }, [currentSection, loadPayments]);

  useEffect(() => {
    if (currentSection === "ocr") {
      loadOcrData();
    }
  }, [currentSection, loadOcrData]);

  useEffect(() => {
    setBidPage(1);
  }, [bidFilters.status, bidFilters.fromDate, bidFilters.toDate]);

  useEffect(() => {
    setIncomePage(1);
  }, [incomeFilter.fromDate, incomeFilter.toDate]);

  useEffect(() => {
    setPaymentPage(1);
  }, [
    paymentFilters.fromDate,
    paymentFilters.mode,
    paymentFilters.search,
    paymentFilters.status,
    paymentFilters.toDate,
  ]);

  useEffect(() => {
    if (bidPage > bidTotalPages) {
      setBidPage(bidTotalPages);
    }
  }, [bidPage, bidTotalPages]);

  useEffect(() => {
    if (incomePage > incomeTotalPages) {
      setIncomePage(incomeTotalPages);
    }
  }, [incomePage, incomeTotalPages]);

  useEffect(() => {
    if (paymentPage > paymentTotalPages) {
      setPaymentPage(paymentTotalPages);
    }
  }, [paymentPage, paymentTotalPages]);

  const personalDocs = useMemo(() => {
    const docs = driver?.docs || {};
    return Object.entries(docs).map(([key, doc]) => ({
      key,
      title: PERSONAL_DOC_LABELS[key] || doc?.title || key,
      uri: doc?.uri || doc?.defaultUri || "",
      identity: doc?.identity || "",
    }));
  }, [driver]);

  return (
    <Layout title={sideBarItems.subscriptionDriver.key}>
      <div className="w-full py-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Driver Admin Details
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Unified admin view for personal data, documents, vehicles, income,
              and ride-share bid history.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => history.push("/subscriptiondriver")}
              className="px-3 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Back to Subscription Control
            </button>
            <button
              type="button"
              onClick={() => history.push("/ridesharebids")}
              className="px-3 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Back to Bid Summary
            </button>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          {driverLoading ? (
            <p className="text-sm text-gray-500">Loading driver profile...</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[160px_1fr] gap-6">
              <div className="flex flex-col items-center gap-3">
                <div className="w-36 h-36 rounded-full overflow-hidden border bg-gray-100 flex items-center justify-center">
                  <ImageS3
                    uri={getImageUrl(driver?.profilePicture || driver?.profile)}
                    style="w-36 h-36 object-cover"
                  />
                </div>
                <div className="text-center">
                  <div className="text-xl font-semibold text-gray-900">
                    {getDisplayName(driver)}
                  </div>
                </div>
              </div>
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {driverSummaryCards.map((item) => (
                    <InfoRow
                      key={item.label}
                      label={item.label}
                      value={item.value}
                    />
                  ))}
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {Object.entries(SECTION_META).map(([key, meta]) => (
                    <SectionButton
                      key={key}
                      label={meta.label}
                      active={currentSection === key}
                      onClick={() => goToSection(key)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {currentSection === "personal" && (
          <div className="space-y-6">
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Personal Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <InfoRow label="Name" value={getDisplayName(driver)} />
                <InfoRow label="First Name" value={driver?.firstName} />
                <InfoRow label="Last Name" value={driver?.lastName} />
                <InfoRow label="Mobile" value={driver?.mobile} />
                <InfoRow label="Email" value={driver?.email} />
                <InfoRow label="Gender" value={driver?.gender} />
                <InfoRow
                  label="Date of Birth"
                  value={formatDateTime(driver?.dob)}
                />
                <InfoRow label="Username" value={driver?.username} />
                <InfoRow label="NID Number" value={driver?.nid} />
                <InfoRow
                  label="Driver Role"
                  value={formatBoolean(driver?.isDriver)}
                />
                <InfoRow
                  label="Owner Role"
                  value={formatBoolean(driver?.isOwner)}
                />
                <InfoRow
                  label="Admin Role"
                  value={formatBoolean(driver?.isAdmin)}
                />
                <InfoRow
                  label="Mobile Verified"
                  value={formatBoolean(driver?.mobileVerified)}
                />
                <InfoRow
                  label="Driver Verified"
                  value={formatBoolean(driver?.isVerifiedAsDriver)}
                />
                <InfoRow
                  label="Owner Verified"
                  value={formatBoolean(driver?.isVerifiedAsOwner)}
                />
                <InfoRow
                  label="Admin Verified"
                  value={formatBoolean(driver?.isVerifiedAsAdmin)}
                />
                <InfoRow
                  label="Trial Enabled"
                  value={formatBoolean(driver?.trialEnabled)}
                />
                <InfoRow
                  label="Access Status"
                  value={driver?.subscriptionAccessStatus}
                />
                <InfoRow
                  label="Freeze Reason"
                  value={driver?.subscriptionFreezeReason}
                />
                <InfoRow
                  label="Frozen At"
                  value={formatDateTime(driver?.subscriptionFrozenAt)}
                />
                <InfoRow
                  label="Ride Share Online"
                  value={formatBoolean(driver?.rideShareStatus?.isOnline)}
                />
                <InfoRow
                  label="Total Rides"
                  value={`${driver?.rideShareStats?.totalRides || 0}`}
                />
                <InfoRow
                  label="Average Rating"
                  value={`${driver?.rideShareStats?.averageRating || 0}`}
                />
                <InfoRow
                  label="Total Ratings"
                  value={`${driver?.rideShareStats?.totalRatings || 0}`}
                />
                <InfoRow
                  label="Completion Rate"
                  value={`${driver?.rideShareStats?.completionRate || 0}`}
                />
                <InfoRow
                  label="Created At"
                  value={formatDateTime(driver?.createdAt)}
                />
              </div>
            </div>

            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Address and Location
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <InfoRow label="City" value={driver?.address?.city} />
                <InfoRow label="District" value={driver?.address?.district} />
                <InfoRow label="Division" value={driver?.address?.division} />
                <InfoRow
                  label="Sub District"
                  value={driver?.address?.subDistrict}
                />
                <InfoRow
                  label="Present Address"
                  value={driver?.address?.presentAddress}
                />
                <InfoRow
                  label="Permanent Address"
                  value={driver?.address?.permanentAddress}
                />
                <InfoRow
                  label="Current Location Name"
                  value={driver?.currentLocation?.name}
                />
                <InfoRow
                  label="Current Latitude"
                  value={`${driver?.currentLocation?.latitude ?? "N/A"}`}
                />
                <InfoRow
                  label="Current Longitude"
                  value={`${driver?.currentLocation?.longitude ?? "N/A"}`}
                />
              </div>
            </div>

            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Personal Documents
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {personalDocs.map((doc) => (
                  <ImageCard
                    key={doc.key}
                    title={doc.title}
                    uri={doc.uri}
                    identity={doc.identity}
                  />
                ))}
                {!personalDocs.length && (
                  <p className="text-sm text-gray-500">
                    No personal documents found.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {currentSection === "ocr" && (
          <div className="space-y-6">
            {ocrLoading ? (
              <div className="bg-white shadow-md rounded-lg p-6">
                <p className="text-sm text-gray-500">
                  Loading OCR extraction data...
                </p>
              </div>
            ) : (
              Object.entries(OCR_DOC_META).map(([documentType, meta]) => {
                const record = ocrData?.[meta.apiKey] || null;
                const form = ocrForm?.[documentType] || {};
                const fields = OCR_FIELD_CONFIG[documentType] || [];
                const isSaving = Boolean(ocrSaving?.[documentType]);
                const sourceDocs = (OCR_SOURCE_DOCS[documentType] || [])
                  .map((item) => {
                    const doc = driver?.docs?.[item.docKey] || {};
                    return {
                      ...item,
                      uri: doc?.uri || doc?.defaultUri || "",
                      identity: doc?.identity || "",
                    };
                  })
                  .filter((item) => Boolean(item.uri));

                return (
                  <div
                    key={documentType}
                    className="bg-white shadow-md rounded-lg p-6"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          {meta.label}
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                          Review, edit, and verify extracted OCR data.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="px-3 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
                        onClick={loadOcrData}
                        disabled={
                          ocrLoading || isSaving || profileVerifyLoading
                        }
                      >
                        Refresh
                      </button>
                    </div>

                    {!record && (
                      <p className="text-sm text-gray-500 mb-4">
                        No OCR extraction found for {meta.label} yet. You can
                        still fill and save all fields manually.
                      </p>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
                      <InfoRow label="Record ID" value={record?.id} />
                      <InfoRow
                        label="Driver Verification Status"
                        value={
                          driver?.isVerifiedAsDriver
                            ? "verified"
                            : "not verified"
                        }
                      />
                      <InfoRow
                        label="Created At"
                        value={formatDateTime(record?.createdAt)}
                      />
                      <InfoRow
                        label="Updated At"
                        value={formatDateTime(record?.updatedAt)}
                      />
                    </div>

                    <div className="mb-5">
                      <h3 className="text-md font-semibold text-gray-900 mb-3">
                        Uploaded Documents
                      </h3>
                      {sourceDocs.length ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {sourceDocs.map((doc) => (
                            <ImageCard
                              key={`${documentType}-${doc.key}`}
                              title={doc.title}
                              uri={doc.uri}
                              identity={doc.identity}
                            />
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          No uploaded documents found for {meta.label}.
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {fields.map((field) => {
                        const value = form?.[field.key] || "";
                        const inputLabel = (
                          <label
                            className="text-xs uppercase tracking-wide text-gray-500"
                            htmlFor={`${documentType}-${field.key}`}
                          >
                            {field.label}
                          </label>
                        );

                        if (
                          field.type === "textarea" ||
                          field.type === "multiline-array"
                        ) {
                          return (
                            <div
                              key={field.key}
                              className="flex flex-col gap-2 md:col-span-2"
                            >
                              {inputLabel}
                              <textarea
                                id={`${documentType}-${field.key}`}
                                className="px-3 py-2 border rounded-md min-h-[110px]"
                                value={value}
                                onChange={(e) =>
                                  updateOcrField(
                                    documentType,
                                    field.key,
                                    e.target.value,
                                  )
                                }
                                placeholder={field.label}
                              />
                            </div>
                          );
                        }

                        return (
                          <div key={field.key} className="flex flex-col gap-2">
                            {inputLabel}
                            <input
                              id={`${documentType}-${field.key}`}
                              className="px-3 py-2 border rounded-md"
                              value={value}
                              onChange={(e) =>
                                updateOcrField(
                                  documentType,
                                  field.key,
                                  e.target.value,
                                )
                              }
                              placeholder={field.label}
                            />
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-5 flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                        onClick={() => saveOcrDocument(documentType)}
                        disabled={isSaving || profileVerifyLoading}
                      >
                        {isSaving ? "Saving..." : "Save Edits"}
                      </button>
                    </div>
                  </div>
                );
              })
            )}

            <div className="bg-white shadow-md rounded-lg p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm text-gray-700">
                  Document Verification Status:{" "}
                  <strong>
                    {driver?.isVerifiedAsDriver ? "verified" : "not verified"}
                  </strong>
                </div>
                <button
                  type="button"
                  className={`px-4 py-2 rounded-md text-white disabled:opacity-60 ${
                    driver?.isVerifiedAsDriver
                      ? "bg-red-500 hover:bg-red-600"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                  onClick={toggleDriverProfileVerification}
                  disabled={ocrLoading || profileVerifyLoading}
                >
                  {profileVerifyLoading
                    ? "Working..."
                    : driver?.isVerifiedAsDriver
                      ? "Cancel Document Verification"
                      : "Verify Document"}
                </button>
              </div>
            </div>
          </div>
        )}

        {currentSection === "vehicles" && (
          <div className="bg-indigo-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Registered Vehicles
            </h3>

            {vehiclesLoading ? (
              <p className="text-sm text-gray-500">Loading vehicles...</p>
            ) : !vehicleRows.length ? (
              <p className="text-sm text-gray-500">No vehicle found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        #
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Vehicle
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Registration
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Seats
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Car Image Verification
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Smart Card
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Tax Token
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Fitness
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Registration Date
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Action
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Delete
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicleRows.map((entry, index) => {
                      const vehicle = entry?.vehicle || {};
                      return (
                        <tr
                          key={vehicle?._id || index}
                          className="border-b hover:bg-gray-50"
                        >
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 font-bold">
                            {vehicle?.verifiedStatus === "verified"
                              ? "✅"
                              : "❌"}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 font-bold">
                            {vehicle?.carInfo?.brand} {vehicle?.carInfo?.model}{" "}
                            {vehicle?.carInfo?.year}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {vehicle?.registration?.cityName}{" "}
                            {vehicle?.registration?.vehicleCategory} -{" "}
                            {vehicle?.registration?.vehicleNumber}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {vehicle?.seats ? vehicle?.seats : "❌"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {vehicle?.docs?.vehicleNumberPlate?.uri ||
                            vehicle?.docs?.vehicleFront?.uri ||
                            vehicle?.docs?.vehicleNumberPlate?.uri ||
                            vehicle?.docs?.vehicleFont?.uri ||
                            vehicle?.docs?.vehicleFront?.uri ||
                            vehicle?.docs?.vehicleFont?.uri
                              ? "✅"
                              : "❌"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {vehicle?.docs?.smartCard?.uri ? "✅" : "❌"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {vehicle?.docs?.taxToken?.uri ? "✅" : "❌"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {vehicle?.docs?.fitness?.uri ? "✅" : "❌"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {vehicle?.createdAt
                              ? moment(vehicle.createdAt).format(
                                  "DD MMM YYYY hh:mm A",
                                )
                              : "N/A"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <button
                              type="button"
                              onClick={() => showVehicleInfo(entry)}
                              className="px-3 py-1 text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition"
                            >
                              Manage
                            </button>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <button
                              type="button"
                              onClick={() => deleteVehicle(entry)}
                              className="py-1 px-3 rounded-md flex items-center gap-2 text-white bg-red-600 hover:bg-red-700 transition"
                            >
                              <FaTrashAlt /> Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {currentSection === "income" && (
          <div className="space-y-6">
            <div className="bg-white shadow-md rounded-lg p-6">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Income Statement
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Driver income summary and monthly breakdown.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 w-full lg:w-auto">
                  <input
                    className="px-3 py-2 border rounded-md"
                    type="date"
                    value={incomeFilter.fromDate}
                    onChange={(e) =>
                      setIncomeFilter((prev) => ({
                        ...prev,
                        fromDate: e.target.value,
                      }))
                    }
                  />
                  <input
                    className="px-3 py-2 border rounded-md"
                    type="date"
                    value={incomeFilter.toDate}
                    onChange={(e) =>
                      setIncomeFilter((prev) => ({
                        ...prev,
                        toDate: e.target.value,
                      }))
                    }
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                      onClick={() => {
                        setIncomePage(1);
                        loadIncome();
                      }}
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                      onClick={() => {
                        const resetFilter = { fromDate: "", toDate: "" };
                        setIncomeFilter(resetFilter);
                        setIncomePage(1);
                      }}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              {incomeLoading ? (
                <p className="text-sm text-gray-500">
                  Loading income history...
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    <InfoRow
                      label="Completed Trips"
                      value={`${incomeData?.summary?.totalTrips || 0}`}
                    />
                    <InfoRow
                      label="Total Driver Income"
                      value={formatMoney(
                        incomeData?.summary?.totalDriverIncome || 0,
                      )}
                    />
                  </div>

                  <div className="mt-6">
                    <h3 className="text-md font-semibold text-gray-900 mb-3">
                      Completed Trip Income History
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full border border-gray-200">
                        <thead>
                          <tr className="bg-gray-50 text-left text-sm text-gray-600">
                            <th className="px-3 py-2 border">Trip ID</th>
                            <th className="px-3 py-2 border">Route</th>
                            <th className="px-3 py-2 border">Trip Date</th>
                            <th className="px-3 py-2 border">Driver Income</th>
                            <th className="px-3 py-2 border">Review</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.isArray(incomeData?.data) &&
                          incomeData.data.length ? (
                            incomeData.data.map((item) => (
                              <tr key={item._id} className="text-sm align-top">
                                <td className="px-3 py-2 border break-all">
                                  {item._id}
                                </td>
                                <td className="px-3 py-2 border">
                                  <div>
                                    From: {item?.fromLocation?.address || "N/A"}
                                  </div>
                                  <div>
                                    To: {item?.toLocation?.address || "N/A"}
                                  </div>
                                </td>
                                <td className="px-3 py-2 border">
                                  <div>{formatDateTime(item.startDate)}</div>
                                </td>
                                <td className="px-3 py-2 border">
                                  {formatMoney(item.driverAmount || 0)}
                                </td>
                                <td className="px-3 py-2 border min-w-[240px]">
                                  {(() => {
                                    const reviewScore =
                                      item?.review?.score ??
                                      item?.ratingScore ??
                                      item?.rating ??
                                      null;
                                    const reviewComment =
                                      item?.review?.comment ??
                                      item?.ratingComment ??
                                      "";

                                    return (
                                      <>
                                        <div className="font-medium text-gray-900">
                                          {reviewScore != null
                                            ? `${
                                                Number(reviewScore) > 5
                                                  ? (
                                                      Number(reviewScore) / 2
                                                    ).toFixed(1)
                                                  : Number(reviewScore).toFixed(
                                                      1,
                                                    )
                                              } /5`
                                            : "No rating"}
                                        </div>
                                        <div className="mt-1 text-xs text-gray-500 whitespace-pre-wrap break-words">
                                          {reviewComment || "No review comment"}
                                        </div>
                                      </>
                                    );
                                  })()}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan="5"
                                className="px-4 py-8 text-center text-gray-500"
                              >
                                No completed trip income history found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="mt-6 overflow-x-auto">
                    <table className="min-w-full border border-gray-200">
                      <thead>
                        <tr className="bg-gray-50 text-left text-sm text-gray-600">
                          <th className="px-3 py-2 border">Month</th>
                          <th className="px-3 py-2 border">Completed Trips</th>
                          <th className="px-3 py-2 border">Income</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(incomeData?.monthlyBreakdown) &&
                        incomeData.monthlyBreakdown.length ? (
                          incomeData.monthlyBreakdown.map((item) => (
                            <tr
                              key={`${item.year}-${item.month}`}
                              className="text-sm"
                            >
                              <td className="px-3 py-2 border">
                                {String(item.month).padStart(2, "0")}/
                                {item.year}
                              </td>
                              <td className="px-3 py-2 border">
                                {item.tripCount || 0}
                              </td>
                              <td className="px-3 py-2 border">
                                {formatMoney(item.income || 0)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="3"
                              className="px-4 py-8 text-center text-gray-500"
                            >
                              No historical income found for the selected
                              period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {incomeTotal > 0 && (
                    <div className="flex justify-center items-center mt-6">
                      <Pagination
                        page={incomePage}
                        totalPages={incomeTotalPages}
                        setPage={setIncomePage}
                        limit={incomeLimit}
                        setLimit={setIncomeLimit}
                        prevPage={() =>
                          setIncomePage((prev) => (prev > 1 ? prev - 1 : prev))
                        }
                        nextPage={() =>
                          setIncomePage((prev) =>
                            prev < incomeTotalPages ? prev + 1 : prev,
                          )
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {currentSection === "bids" && (
          <div className="space-y-6">
            <div className="bg-white shadow-md rounded-lg p-6">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Ride Share Bid History
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Complete bid records for this driver with ride and passenger
                    details.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 w-full lg:w-auto">
                  <select
                    className="px-3 py-2 border rounded-md"
                    value={bidFilters.status}
                    onChange={(e) =>
                      setBidFilters((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                  >
                    {BID_STATUS_OPTIONS.map((item) => (
                      <option key={item.value || "all"} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <input
                    className="px-3 py-2 border rounded-md"
                    type="date"
                    value={bidFilters.fromDate}
                    onChange={(e) =>
                      setBidFilters((prev) => ({
                        ...prev,
                        fromDate: e.target.value,
                      }))
                    }
                  />
                  <input
                    className="px-3 py-2 border rounded-md"
                    type="date"
                    value={bidFilters.toDate}
                    onChange={(e) =>
                      setBidFilters((prev) => ({
                        ...prev,
                        toDate: e.target.value,
                      }))
                    }
                  />
                  <button
                    type="button"
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    onClick={() => {
                      setBidFilters({ status: "", fromDate: "", toDate: "" });
                      setBidPage(1);
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
                <InfoRow
                  label="Total Bids"
                  value={`${bidSummary?.total || 0}`}
                />
                <InfoRow
                  label="Accepted"
                  value={`${bidSummary?.accepted || 0}`}
                />
                <InfoRow
                  label="Rejected"
                  value={`${bidSummary?.rejected || 0}`}
                />
                <InfoRow
                  label="Created"
                  value={`${bidSummary?.created || 0}`}
                />
                <InfoRow
                  label="Expired"
                  value={`${bidSummary?.expired || 0}`}
                />
                <InfoRow
                  label="Total Bid Amount"
                  value={formatMoney(bidSummary?.totalBidAmount || 0)}
                />
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50 text-left text-sm text-gray-600">
                      <th className="px-3 py-2 border">Bid</th>
                      <th className="px-3 py-2 border">Passenger</th>
                      <th className="px-3 py-2 border">Ride</th>
                      <th className="px-3 py-2 border">Amount</th>
                      <th className="px-3 py-2 border">Status</th>
                      <th className="px-3 py-2 border">Timestamps</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bids.map((bid) => (
                      <tr key={bid.bidId} className="text-sm align-top">
                        <td className="px-3 py-2 border break-all">
                          <div className="font-medium">
                            {bid.bidId || "N/A"}
                          </div>
                          <div className="text-xs text-gray-500 break-all">
                            Ride: {bid.rideId || "N/A"}
                          </div>
                        </td>
                        <td className="px-3 py-2 border">
                          <div className="font-medium">
                            {bid?.passenger?.name || "N/A"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {bid?.passenger?.mobile || "N/A"}
                          </div>
                        </td>
                        <td className="px-3 py-2 border">
                          <div>
                            <span className="font-medium">Ride Status:</span>{" "}
                            {bid?.ride?.status || "N/A"}
                          </div>
                          <div>
                            <span className="font-medium">Vehicle Type:</span>{" "}
                            {bid?.ride?.vehicleType || "N/A"}
                          </div>
                          <div className="mt-1 text-xs text-gray-600">
                            <div>
                              Pickup: {bid?.ride?.pickupAddress || "N/A"}
                            </div>
                            <div>
                              Dropoff: {bid?.ride?.dropoffAddress || "N/A"}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 border">
                          {formatMoney(bid.amount)}
                        </td>
                        <td className="px-3 py-2 border">
                          {bid.status || "N/A"}
                        </td>
                        <td className="px-3 py-2 border text-xs text-gray-600">
                          <div>Created: {formatDateTime(bid.createdAt)}</div>
                          <div>Accepted: {formatDateTime(bid.acceptedAt)}</div>
                          <div>Rejected: {formatDateTime(bid.rejectedAt)}</div>
                          <div>Expired: {formatDateTime(bid.expiredAt)}</div>
                          <div>
                            Cancelled: {formatDateTime(bid.cancelledAt)}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!bidLoading && !bids.length && (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-4 py-8 text-center text-gray-500"
                        >
                          No bids found for this driver.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {bidLoading && (
                <p className="text-sm text-gray-500 mt-4">Loading bids...</p>
              )}

              {bidTotal > 0 && (
                <div className="flex justify-center items-center mt-6">
                  <Pagination
                    page={bidPage}
                    totalPages={bidTotalPages}
                    setPage={setBidPage}
                    limit={bidLimit}
                    setLimit={setBidLimit}
                    prevPage={() =>
                      setBidPage((prev) => (prev > 1 ? prev - 1 : prev))
                    }
                    nextPage={() =>
                      setBidPage((prev) =>
                        prev < bidTotalPages ? prev + 1 : prev,
                      )
                    }
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {currentSection === "payments" && (
          <div className="space-y-6">
            <div className="bg-white shadow-md rounded-lg p-6">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Subscription Payments
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Payment records for this specific driver.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-2 w-full lg:w-auto">
                  <input
                    className="px-3 py-2 border rounded-md"
                    type="text"
                    placeholder="Search payment or txn ref"
                    value={paymentFilters.search}
                    onChange={(e) =>
                      setPaymentFilters((prev) => ({
                        ...prev,
                        search: e.target.value,
                      }))
                    }
                  />

                  <select
                    className="px-3 py-2 border rounded-md"
                    value={paymentFilters.status}
                    onChange={(e) =>
                      setPaymentFilters((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                  >
                    {PAYMENT_STATUS_OPTIONS.map((item) => (
                      <option
                        key={item.value || "all-status"}
                        value={item.value}
                      >
                        {item.label}
                      </option>
                    ))}
                  </select>

                  <select
                    className="px-3 py-2 border rounded-md"
                    value={paymentFilters.mode}
                    onChange={(e) =>
                      setPaymentFilters((prev) => ({
                        ...prev,
                        mode: e.target.value,
                      }))
                    }
                  >
                    {PAYMENT_MODE_OPTIONS.map((item) => (
                      <option key={item.value || "all-mode"} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>

                  <input
                    className="px-3 py-2 border rounded-md"
                    type="date"
                    value={paymentFilters.fromDate}
                    onChange={(e) =>
                      setPaymentFilters((prev) => ({
                        ...prev,
                        fromDate: e.target.value,
                      }))
                    }
                  />

                  <input
                    className="px-3 py-2 border rounded-md"
                    type="date"
                    value={paymentFilters.toDate}
                    onChange={(e) =>
                      setPaymentFilters((prev) => ({
                        ...prev,
                        toDate: e.target.value,
                      }))
                    }
                  />

                  <button
                    type="button"
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    onClick={() => {
                      setPaymentFilters({
                        status: "",
                        mode: "",
                        fromDate: "",
                        toDate: "",
                        search: "",
                      });
                      setPaymentPage(1);
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                <InfoRow
                  label="Total Payments"
                  value={`${paymentSummary?.totalCount || 0}`}
                />
                <InfoRow
                  label="Successful"
                  value={`${paymentSummary?.successCount || 0}`}
                />
                <InfoRow
                  label="Failed"
                  value={`${paymentSummary?.failedCount || 0}`}
                />
                <InfoRow
                  label="Initiated"
                  value={`${paymentSummary?.initiatedCount || 0}`}
                />
                <InfoRow
                  label="Success Amount"
                  value={formatMoney(paymentSummary?.successAmount || 0)}
                />
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50 text-left text-sm text-gray-600">
                      <th className="px-3 py-2 border">Payment</th>
                      <th className="px-3 py-2 border">Type</th>
                      <th className="px-3 py-2 border">Plan</th>
                      <th className="px-3 py-2 border">Amount</th>
                      <th className="px-3 py-2 border">Status</th>
                      <th className="px-3 py-2 border">Method</th>
                      <th className="px-3 py-2 border">References</th>
                      <th className="px-3 py-2 border">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment?._id} className="text-sm align-top">
                        <td className="px-3 py-2 border break-all">
                          <div className="font-medium">
                            {payment?._id || "N/A"}
                          </div>
                          <div className="text-xs text-gray-500">
                            Platform: {payment?.appPlatform || "N/A"}
                          </div>
                        </td>
                        <td className="px-3 py-2 border">
                          {getPaymentTypeLabel(payment)}
                        </td>
                        <td className="px-3 py-2 border">
                          <div className="font-medium">
                            {payment?.subscriptionPack?.name || "N/A"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {payment?.subscriptionPack?.durationDays || 0} days
                          </div>
                        </td>
                        <td className="px-3 py-2 border">
                          {formatMoney(
                            payment?.amount || payment?.paidAmount || 0,
                          )}
                        </td>
                        <td className="px-3 py-2 border">
                          {payment?.status || "N/A"}
                        </td>
                        <td className="px-3 py-2 border">
                          {String(
                            payment?.paymentMethod || "N/A",
                          ).toUpperCase()}
                        </td>
                        <td className="px-3 py-2 border text-xs text-gray-600 break-all">
                          <div>
                            Provider: {payment?.providerTransactionId || "N/A"}
                          </div>
                          <div>
                            bKash ID: {payment?.bkashPaymentId || "N/A"}
                          </div>
                          <div>
                            Idempotency: {payment?.idempotencyKey || "N/A"}
                          </div>
                        </td>
                        <td className="px-3 py-2 border text-xs text-gray-600">
                          <div>{formatDateTime(payment?.createdAt)}</div>
                          <div>
                            Updated: {formatDateTime(payment?.updatedAt)}
                          </div>
                        </td>
                      </tr>
                    ))}

                    {!paymentLoading && !payments.length && (
                      <tr>
                        <td
                          colSpan="8"
                          className="px-4 py-8 text-center text-gray-500"
                        >
                          No subscription payments found for this driver.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {paymentLoading && (
                <p className="text-sm text-gray-500 mt-4">
                  Loading payment history...
                </p>
              )}

              {paymentTotal > 0 && (
                <div className="flex justify-center items-center mt-6">
                  <Pagination
                    page={paymentPage}
                    totalPages={paymentTotalPages}
                    setPage={setPaymentPage}
                    limit={paymentLimit}
                    setLimit={setPaymentLimit}
                    prevPage={() =>
                      setPaymentPage((prev) => (prev > 1 ? prev - 1 : prev))
                    }
                    nextPage={() =>
                      setPaymentPage((prev) =>
                        prev < paymentTotalPages ? prev + 1 : prev,
                      )
                    }
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {currentSection === "device" && <DeviceInfoSection driver={driver} />}

        {vehicleManageModalOpen && selectedVehicleEntry?.vehicle ? (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]"
            onClick={() => handleVehicleModal(false)}
          >
            <div
              className="bg-white rounded-lg p-4 max-w-[95vw] max-h-[90vh] overflow-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                aria-label="Close modal"
                className="absolute top-2 right-2 h-8 w-8 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
                onClick={() => handleVehicleModal(false)}
              >
                ×
              </button>
              <CarInfo
                handleModal={handleVehicleModal}
                ownerInfo={driver}
                carInfo={selectedVehicleEntry}
              />
            </div>
          </div>
        ) : null}
      </div>
    </Layout>
  );
}

export default withRouter(DriverAdminDetails);
